import './infoCard-main.scss';

import { cn } from '@sola/ark-core/utils/helpers.ts';
import { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.ts';

import { CardList } from '../../../molecules/card-list/card-list.tsx';
import { Card } from '../../../molecules/card/card.tsx';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../../../molecules/carousel/carousel.tsx';
import { FeatureBase } from '../../../molecules/feature-base/feature-base.tsx';
import { FeatureButtons } from '../../../molecules/feature-buttons/feature-buttons.tsx';
import { FeatureCard } from '../../../molecules/feature-card/feature-card.tsx';
import { HeaderGroup } from '../../../molecules/header-group/header-group.tsx';
import { getPlaceholderItems } from '../../../utils/placeholder-items.tsx';
import { getBaseSizeProps } from '../../../utils/text-utils.tsx';

export const InfoCardMain: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  quotation: _quotation,
  primaryCTA,
  secondaryCTA,
  tertiaryCTA,
  items,
  alt,
  ...props
}) => {
  const infoItems =
    !items || items.length < 2 ? getPlaceholderItems(items, 2) : items;

  const isCentered = alt;
  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image && !isCentered;
  const hasHeaderGroup = hasHeaderContent && !media?.image && !isCentered;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const hasButtons =
    (!hasHeaderCard && (!!primaryCTA || !!secondaryCTA)) || !!tertiaryCTA;

  const getCardColumns = () => {
    if (infoItems.length <= 4) {
      return infoItems.length;
    }
    if (infoItems.length % 3 === 0) {
      return 3;
    }
    return 2;
  };
  const cardColumns = getCardColumns();

  return (
    <section
      className={cn(
        'InfoCardMain component',
        items && items.length > 0 ? 'has-items' : 'no-items',
        {
          'InfoCardMain--alt': alt,
        },
      )}
    >
      <div className="InfoCardMain__container container flex flex-col w-full max-w-xl mx-auto gap-comfy-xl md:gap-roomy-xs lg:gap-roomy-sm">
        {hasHeaderCard && !isCentered && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            hasPlaceholder={alt}
            inline
          />
        )}
        {hasHeaderGroup && !isCentered && (
          <HeaderGroup {...props} seoLevel={seoLevel} />
        )}
        {hasHeaderContent && isCentered && (
          <div className={cn('w-full max-w-md mx-auto')}>
            <FeatureBase
              {...props}
              {...getBaseSizeProps(headerDisplayLevel)}
              seoLevel={seoLevel}
              variant="centered"
              inline
            />
          </div>
        )}

        {infoItems.length > 4 && (
          <div
            className="InfoCardMain__carousel"
            data-length={infoItems.length}
          >
            <Carousel
              aria-label={`Carousel: ${props.title}}`}
              opts={{
                align: 'start',
                loop: false,
                watchFocus: false,
              }}
            >
              <CarouselContent>
                {infoItems.map(
                  (
                    {
                      media: _mediaSlide,
                      quotation: _quotationSlide,
                      ...slide
                    },
                    index,
                  ) => (
                    <CarouselItem
                      key={['slide', index, slide.title].join('-')}
                      index={index}
                      className="InfoCardMain__slide"
                      aria-label={
                        slide.title || `${index + 1} of ${infoItems.length}`
                      }
                    >
                      <Card
                        key={index}
                        {...slide}
                        {...getBaseSizeProps(4)}
                        seoLevel={hasHeaderContent ? seoLevel + 1 : seoLevel}
                        icon={slide.icon || 'alert-circle'}
                        variant="icon"
                      />
                    </CarouselItem>
                  ),
                )}
              </CarouselContent>
              <div className="flex justify-end gap-comfy-sm mt-comfy-lg">
                <CarouselPrevious />
                <CarouselNext />
              </div>
            </Carousel>
          </div>
        )}

        {infoItems.length <= 4 && (
          <div className="InfoCardMain__grid" data-length={infoItems.length}>
            <CardList
              columnsLg={cardColumns}
              columnsMd={cardColumns === 3 ? 3 : 2}
              columns={1}
            >
              {infoItems?.map(
                (
                  { media: mediaItem, quotation: quotationItem, ...item },
                  index,
                ) => {
                  return (
                    <Card
                      key={index}
                      {...item}
                      seoLevel={hasHeaderContent ? seoLevel + 1 : seoLevel}
                      icon={item.icon || 'alert-circle'}
                      variant="icon"
                      titleSize={infoItems.length > 3 ? 'xs' : 'sm'}
                      bodySize="sm"
                      buttonSize="md"
                    />
                  );
                },
              )}
            </CardList>
          </div>
        )}

        {hasButtons && (
          <div className={cn('InfoCardMain__ctas mt-comfy-xs')}>
            <FeatureButtons
              {...{ primaryCTA, secondaryCTA, tertiaryCTA }}
              buttonSize="lg"
              isLinks={!primaryCTA && !secondaryCTA}
              alignment={isCentered ? 'center' : 'left'}
              inline
            />
          </div>
        )}
      </div>
    </section>
  );
};
